import { useMemo, useRef, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { servicesAPI, ordersAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import AdsDisplay from '../components/AdsDisplay';
import { Zap, AlertCircle, Search, X } from 'react-feather';
import { formatCurrencyWithConversion } from '../utils/currency';
import { getServiceColor, getServiceIcon, getServiceIconAsset, getServiceToken } from '../utils/serviceIcons';
import './NewOrder.css';

export default function NewOrder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [servicesMap, setServicesMap] = useState({});
  const [flat, setFlat] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [form, setForm] = useState({ service_id: '', link: '', quantity: '', comments: '' });
  const [selectedService, setSelectedService] = useState(null);
  const [costUSD, setCostUSD] = useState(0);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const commentNumbersRef = useRef(null);
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    servicesAPI.getAll()
      .then(r => {
        setServicesMap(r.data.services);
        setFlat(r.data.flat);
        const cats = Object.keys(r.data.services);
        setCategories(cats);
        if (cats.length) setSelectedCategory(cats[0]);
      })
      .catch(() => setError('Failed to load services.'))
      .finally(() => setServicesLoading(false));
  }, []);

  useEffect(() => {
    if (!categories.length || !flat.length) return;

    const requestedCategory = searchParams.get('category');
    const requestedServiceId = searchParams.get('service_id');

    if (requestedCategory && categories.includes(requestedCategory) && requestedCategory !== selectedCategory) {
      setSelectedCategory(requestedCategory);
      setSearchQuery(requestedCategory);
    }

    if (requestedServiceId) {
      const matchedService = flat.find((service) => String(service.id) === String(requestedServiceId));
      if (matchedService) {
        if (matchedService.category !== selectedCategory) {
          setSelectedCategory(matchedService.category);
          setSearchQuery(matchedService.category);
        }
        if (form.service_id !== String(matchedService.id)) {
          setForm((current) => ({ ...current, service_id: String(matchedService.id) }));
        }
      }
    }
  }, [categories, flat, searchParams, selectedCategory, form.service_id]);

  useEffect(() => {
    if (form.service_id) {
      const svc = flat.find(s => s.id === parseInt(form.service_id, 10));
      setSelectedService(svc || null);
      if (svc) setForm(f => ({ ...f, quantity: String(svc.min), comments: '' }));
    } else {
      setSelectedService(null);
    }
  }, [form.service_id, flat]);

  useEffect(() => {
    if (selectedService && form.quantity) {
      const qty = parseInt(form.quantity, 10) || 0;
      const priceValue = selectedService.final_price;
      const priceUSD = typeof priceValue === 'string' ? parseFloat(priceValue) : priceValue || 0;
      setCostUSD((qty / 1000) * priceUSD);
    } else {
      setCostUSD(0);
    }
  }, [selectedService, form.quantity]);

  const balanceUSD = parseFloat(user?.balance || 0);
  const canAfford = costUSD > 0 && balanceUSD >= costUSD;
  const qty = parseInt(form.quantity, 10) || 0;
  const qtyValid = selectedService
    ? qty >= selectedService.min && qty <= selectedService.max
    : true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!qtyValid) {
      setError(`Quantity must be between ${selectedService.min.toLocaleString()} and ${selectedService.max.toLocaleString()}`);
      return;
    }

    if (selectedService?.custom_comments_enabled && !form.comments.trim()) {
      setError('Custom comments are required for this service');
      return;
    }

    if (!canAfford) {
      setError(`Insufficient balance. You need ${formatCurrencyWithConversion(costUSD, user?.currency || 'USD', 4)} but have ${formatCurrencyWithConversion(balanceUSD, user?.currency || 'USD', 4)}`);
      return;
    }

    setLoading(true);
    try {
      const res = await ordersAPI.place({
        service_id: parseInt(form.service_id, 10),
        link: form.link,
        quantity: qty,
        comments: form.comments
      });

      const discountNote = res.data.referral_discount_applied
        ? ` | Referral discount: ${res.data.referral_discount_percent}% saved ${formatCurrencyWithConversion(parseFloat(res.data.referral_discount_amount || 0), user?.currency || 'USD', 4)}`
        : '';
      setSuccess(`Order #${res.data.order_id} placed. Cost: ${formatCurrencyWithConversion(parseFloat(res.data.cost), user?.currency || 'USD', 4)} | New balance: ${formatCurrencyWithConversion(parseFloat(res.data.new_balance), user?.currency || 'USD', 4)}${discountNote}`);
      setForm(f => ({ ...f, link: '', quantity: selectedService ? String(selectedService.min) : '', comments: '' }));
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const categoryServices = servicesMap[selectedCategory] || [];
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredCategories = normalizedSearch
    ? categories.filter((category) => category.toLowerCase().includes(normalizedSearch))
    : categories;
  const filteredServices = categoryServices;
  const commentLineNumbers = useMemo(() => {
    const lineCount = Math.max(1, form.comments.split('\n').length);
    return Array.from({ length: lineCount }, (_, index) => index + 1);
  }, [form.comments]);
  const enteredCommentCount = form.comments
    .split('\n')
    .filter(comment => comment.trim()).length;

  const handleCommentsScroll = (event) => {
    if (commentNumbersRef.current) {
      commentNumbersRef.current.scrollTop = event.currentTarget.scrollTop;
    }
  };

  const renderCategoryOption = (categoryName) => {
    const iconToken = getServiceToken(categoryName);
    const icon = getServiceIcon(iconToken);
    const iconColor = getServiceColor(iconToken);
    const iconAsset = getServiceIconAsset(iconToken);

    return (
      <>
        <span
          className="new-order-category-icon"
          style={{ background: `${iconColor}18`, color: iconColor, borderColor: `${iconColor}33` }}
          aria-hidden="true"
        >
          {iconAsset ? (
            <img src={iconAsset} alt="" className="new-order-category-icon-image" />
          ) : (
            icon
          )}
        </span>
        <span>{categoryName}</span>
      </>
    );
  };

  return (
    <div>
      <h1 className="page-title">New Order</h1>
      <p className="page-subtitle">Select a service and boost your social media presence</p>
      <div className="alert alert-error" style={{ marginBottom: 16 }}>
        Avoid placing too many orders at once. Place a few orders, wait for them to start or complete, then continue.
      </div>

      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />

      <AdsDisplay adSlot="banner-320x50" className="banner" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24, alignItems: 'start' }}>
        <div className="card">
          {error && <div className="alert alert-error">{error}</div>}
          {success && (
            <div className="alert alert-success">
              {success}
              <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                <Link to="/dashboard/orders" className="btn btn-secondary btn-sm">View Orders</Link>
                <button className="btn btn-secondary btn-sm" onClick={() => setSuccess('')}>Place Another</button>
              </div>
            </div>
          )}

          {servicesLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
              <p style={{ marginTop: 12, color: 'var(--text-muted)' }}>Loading services...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <div className="new-order-category-search">
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Search size={18} style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }} />
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Search category, e.g. Facebook"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowCategorySuggestions(true);
                      }}
                      onFocus={() => setShowCategorySuggestions(true)}
                      style={{ paddingLeft: 40, paddingRight: 40 }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery('');
                          setShowCategorySuggestions(true);
                        }}
                        style={{
                          position: 'absolute',
                          right: 12,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-muted)'
                        }}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  {showCategorySuggestions && (
                    <div className="new-order-category-suggestions">
                      {filteredCategories.length > 0 ? (
                        filteredCategories.map((category) => (
                          <button
                            key={category}
                            type="button"
                            className={`new-order-category-suggestion ${category === selectedCategory ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedCategory(category);
                              setSearchQuery(category);
                              setForm((f) => ({ ...f, service_id: '' }));
                              setSelectedService(null);
                              setShowCategorySuggestions(false);
                              setSearchParams((current) => {
                                const next = new URLSearchParams(current);
                                next.set('category', category);
                                next.delete('service_id');
                                return next;
                              }, { replace: true });
                            }}
                          >
                            {renderCategoryOption(category)}
                          </button>
                        ))
                      ) : (
                        <div className="new-order-category-empty">No categories match your search</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {selectedService ? (
                <div className="card" style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, marginBottom: 16 }}>Service Info</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {[
                      ['Service ID', `#${selectedService.id}`],
                      ['Category', selectedService.category],
                      ['Average Delivery Time', selectedService.average_time || 'Not provided'],
                      ['Price / 1k', formatCurrencyWithConversion(typeof selectedService.final_price === 'string' ? parseFloat(selectedService.final_price) : selectedService.final_price || 0, user?.currency || 'USD', 4)],
                      ['Min Order', selectedService.min.toLocaleString()],
                      ['Max Order', selectedService.max.toLocaleString()],
                    ].map(([k, v]) => (
                      <div
                        key={k}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 0',
                          borderBottom: '1px solid var(--border)',
                          fontSize: 14
                        }}
                      >
                        <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                        <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
                      </div>
                    ))}
                    {selectedService.description && (
                      <div style={{ paddingTop: 12 }}>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Description</div>
                        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)' }}>{selectedService.description}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="card" style={{ marginBottom: 16, background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                <h4 style={{ fontSize: 14, marginBottom: 12, color: 'var(--text-muted)' }}>Tips</h4>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    'Make sure your profile or post is public',
                    'Do not change the URL after placing the order',
                    'Start count is recorded before delivery',
                    'Auto-refunds apply to canceled or partial orders',
                  ].map((tip, i) => (
                    <li key={i} style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 8 }}>
                      <span style={{ color: 'var(--accent)', flexShrink: 0 }}>•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="form-group">
                <label className="form-label">Service</label>
                <select
                  className="form-select"
                  value={form.service_id}
                  onChange={e => {
                    const nextServiceId = e.target.value;
                    setForm(f => ({ ...f, service_id: nextServiceId }));
                    setSearchParams((current) => {
                      const next = new URLSearchParams(current);
                      if (selectedCategory) next.set('category', selectedCategory);
                      if (nextServiceId) next.set('service_id', nextServiceId);
                      else next.delete('service_id');
                      return next;
                    }, { replace: true });
                  }}
                  required
                >
                  <option value="">Select a service</option>
                  {filteredServices.length > 0 ? (
                    filteredServices.map(s => (
                      <option key={s.id} value={s.id}>
                        [{s.id}] {s.name} - {formatCurrencyWithConversion(typeof s.final_price === 'string' ? parseFloat(s.final_price) : s.final_price || 0, user?.currency || 'USD', 4)}/1k
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No services match your search</option>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Link / URL</label>
                <input
                  className="form-input"
                  type="url"
                  value={form.link}
                  onChange={e => setForm(f => ({ ...f, link: e.target.value }))}
                  placeholder="https://instagram.com/yourprofile"
                  required
                />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Paste the exact URL of the post, profile, or page
                </div>
              </div>

              {selectedService?.custom_comments_enabled ? (
                <div className="form-group">
                  <label className="form-label">Custom Comments</label>
                  <div className="custom-comments-editor">
                    <div className="custom-comments-line-numbers" ref={commentNumbersRef} aria-hidden="true">
                      {commentLineNumbers.map(number => (
                        <div key={number}>{number}</div>
                      ))}
                    </div>
                    <textarea
                      className="form-input custom-comments-textarea"
                      value={form.comments}
                      onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
                      onScroll={handleCommentsScroll}
                      placeholder="Enter your custom comments here. Put each comment on a new line when needed."
                      rows={5}
                      required
                      wrap="off"
                    />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {enteredCommentCount.toLocaleString()} comment{enteredCommentCount === 1 ? '' : 's'} entered. Add each comment on a new line.
                  </div>
                </div>
              ) : null}

              <div className="form-group">
                <label className="form-label">
                  Quantity
                  {selectedService && (
                    <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
                      (min {selectedService.min.toLocaleString()} - max {selectedService.max.toLocaleString()})
                    </span>
                  )}
                </label>
                <input
                  className="form-input"
                  style={!qtyValid && form.quantity ? { borderColor: 'var(--red-500)' } : {}}
                  type="number"
                  value={form.quantity}
                  onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                  min={selectedService?.min}
                  max={selectedService?.max}
                  placeholder="Enter quantity"
                  required
                />
                {!qtyValid && form.quantity && (
                  <div style={{ fontSize: 12, color: 'var(--red-500)', marginTop: 4 }}>
                    <AlertCircle size={12} style={{ display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} />
                    Quantity out of range ({selectedService?.min?.toLocaleString()} - {selectedService?.max?.toLocaleString()})
                  </div>
                )}
              </div>

              <div
                style={{
                  background: 'var(--bg-elevated)',
                  borderRadius: 'var(--radius)',
                  padding: '16px 18px',
                  marginBottom: 20,
                  border: '1px solid var(--border)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Price per 1,000</span>
                  <span style={{ fontWeight: 600 }}>
                    {selectedService ? formatCurrencyWithConversion(parseFloat(selectedService.final_price) || 0, user?.currency || 'USD', 4) : '-'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Quantity</span>
                  <span style={{ fontWeight: 600 }}>{qty > 0 ? qty.toLocaleString() : '-'}</span>
                </div>
                <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 800, fontFamily: 'Syne, sans-serif' }}>
                  <span>Total Cost</span>
                  <span style={{ color: costUSD > 0 ? 'var(--orange)' : 'var(--text-muted)' }}>
                    {formatCurrencyWithConversion(costUSD, user?.currency || 'USD', 4)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Your Balance</span>
                  <span style={{
                    fontWeight: 700,
                    color: costUSD > 0 && !canAfford ? 'var(--red-500)' : 'var(--green-500)'
                  }}>
                    {formatCurrencyWithConversion(balanceUSD, user?.currency || 'USD', 4)}
                    {costUSD > 0 && !canAfford && <> <AlertCircle size={12} style={{ display: 'inline-block', verticalAlign: 'middle' }} /> insufficient</>}
                  </span>
                </div>
                {costUSD > 0 && !canAfford && (
                  <Link
                    to="/dashboard/deposit"
                    style={{ display: 'block', marginTop: 10, textAlign: 'center', fontSize: 13, color: 'var(--orange)', textDecoration: 'none', fontWeight: 600 }}
                  >
                    Add {formatCurrencyWithConversion(costUSD - balanceUSD, user?.currency || 'USD', 4)} more {'→'}
                  </Link>
                )}
              </div>

              <button
                className="btn btn-primary btn-lg new-order-submit-btn"
                disabled={loading || !form.service_id || !form.link || !qtyValid || !canAfford}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Placing order...
                  </span>
                ) : (
                  <>
                    <Zap size={16} style={{ marginRight: 6, display: 'inline-block', verticalAlign: 'middle' }} /> Place Order
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        <div />
      </div>
    </div>
  );
}
